/**
 * stubTypes — placeholder type definitions for catalogs/operations whose
 * full schemas haven't been formalized yet but whose consumer code already
 * imports them.
 *
 * Strategy:
 *   - Names match the import sites verbatim.
 *   - Shapes are intentionally LOOSE — most fields are optional with `unknown`-
 *     equivalent types so the consumers compile without forcing every panel
 *     to be rewritten. Tighten field-by-field as the wire contracts firm up.
 *   - When a consumer file lands a full schema for one of these, MOVE the
 *     interface into its proper home (admin.ts / signing.ts / fraud.ts /
 *     config/index.ts) and delete the stub here.
 */

import type { Iso8601 } from './common.js';

// ─────────────────────────────────────────────────────────────────────────
// Wet-sign FSR attestation (WetSignScanner.tsx)
// ─────────────────────────────────────────────────────────────────────────

export interface WetSignAttestation {
  IdTypeVerified: string;
  IdLast4: string;
  WitnessedSignature: boolean;
  OriginalDocument: boolean;
  DateMatches: boolean;
  DateMismatchExplanation?: string;
  AttestingOfficerCode: string;
  AttestationTimestamp: Iso8601;
}

// ─────────────────────────────────────────────────────────────────────────
// Compliance rules engine (compliance editors + hooks)
// ─────────────────────────────────────────────────────────────────────────

export interface ComplianceGate {
  code: string;
  label?: string;
  description?: string;
  severity?: 'info' | 'warn' | 'block';
  isActive?: boolean;
  sortOrder?: number;
  updatedAt?: Iso8601 | null;
  rowVersion?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export type ComplianceTriggerEvent = string;

export interface ComplianceRuleApplication {
  ruleId: number;
  gateCode: string;
  triggerEvent?: ComplianceTriggerEvent;
  /** Free-form predicate JSON read by the rules engine. */
  predicateJson?: string;
  decision?: string;
  rationale?: string;
  isActive?: boolean;
  sortOrder?: number;
  updatedAt?: Iso8601 | null;
  rowVersion?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

// ─────────────────────────────────────────────────────────────────────────
// Signing-engine routing (signingEngineRoutingEditor.tsx)
// ─────────────────────────────────────────────────────────────────────────

export type SigningEngine = string;

export type SigningSurface = string;

export interface SigningEngineRoutingRow {
  rowId?: number | string;
  routingId: string;
  surface: SigningSurface;
  cohort?: string | null;
  cohortKey: string;
  docTypeCode: string;
  engine: SigningEngine;
  priority?: number;
  isActive: boolean;
  reason?: string;
  effectiveFrom?: Iso8601 | null;
  modifiedAt?: Iso8601 | null;
  modifiedBy?: string | null;
  updatedAt?: Iso8601 | null;
  updatedBy?: string | null;
  createdAt?: Iso8601 | null;
  createdBy?: string | null;
  rowVersion?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface SigningEngineRoutingUpsertBody {
  rowId?: number | string;
  routingId?: string;
  surface: SigningSurface;
  cohort?: string | null;
  cohortKey: string;
  docTypeCode: string;
  engine: SigningEngine;
  priority?: number;
  isActive: boolean;
  reason?: string;
  effectiveFrom?: Iso8601 | null | string;
}

// ─────────────────────────────────────────────────────────────────────────
// Data-maintenance operations panel
// ─────────────────────────────────────────────────────────────────────────

export type ArchivalSweepScope = string;

export const ARCHIVAL_SWEEP_SCOPES: ReadonlyArray<ArchivalSweepScope> = [
  'expired-carts',
  'expired-documents',
  'expired-signing-sessions',
  'orphan-uploads',
  'completed-tasks',
  'kill-list-purge',
  'expired-lobby',
  'abandoned-carts',
];

export interface ArchivalSweepInput {
  scopes?: ReadonlyArray<ArchivalSweepScope>;
  dryRun?: boolean;
  olderThanDays?: number;
  cutoffDays?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

/** Per-scope row inside an ArchivalSweepOutcome.scopes array. */
export interface ArchivalSweepScopeRow {
  scope: ArchivalSweepScope;
  matched: number;
  archived: number;
  description: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface ArchivalSweepOutcome {
  success: boolean;
  scope?: ArchivalSweepScope;
  /** Per-scope breakdown returned by the sweep job. */
  scopes: ReadonlyArray<ArchivalSweepScopeRow>;
  scanned?: number;
  archived?: number;
  errors?: ReadonlyArray<{ key: string; message: string }>;
  warnings?: ReadonlyArray<string>;
  softArchivalMode?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface SampleDataCohortMix {
  personal: number;
  business: number;
  trust: number;
  fiduciary?: number;
  charitable?: number;
  governmental?: number;
  [k: string]: number | undefined;
}

export interface SampleDataGenerateOutcome {
  success: boolean;
  cartsCreated?: number;
  peopleCreated?: number;
  accountsCreated?: number;
  /** Per-cohort generated counts. */
  generated: SampleDataCohortMix;
  generatedAt: Iso8601;
  elapsedMs: number;
  cartIds: ReadonlyArray<string>;
  cifNumbers: ReadonlyArray<string>;
  warnings?: ReadonlyArray<string>;
  mix?: SampleDataCohortMix;
  error?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface TestModeCounts {
  carts: number;
  people?: number;
  documents?: number;
  signingSessions: number;
  signingSigners: number;
  tasks: number;
  cartCifIndex: number;
  pipelineStepExecutions: number;
  sessionEvents: number;
  total: number;
  [k: string]: number | undefined;
}

export interface TestModePurgeOutcome {
  success: boolean;
  before?: TestModeCounts;
  after?: TestModeCounts;
  deleted: TestModeCounts;
  durationMs?: number;
  elapsedMs: number;
  purgedAt: Iso8601;
  error?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

// ─────────────────────────────────────────────────────────────────────────
// Entra user-sync panel
// ─────────────────────────────────────────────────────────────────────────

export interface EntraUserRow {
  upn: string;
  email?: string | null;
  displayName: string;
  jobTitle?: string | null;
  department?: string | null;
  branchCode?: string | null;
  officerCode?: string | null;
  accountEnabled?: boolean;
  entraObjectId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface EntraSyncFieldDiff {
  field?: string;
  fieldName: string;
  oldValue?: string | null;
  newValue?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface EntraSyncUpdateRow {
  upn?: string;
  email: string;
  displayName?: string;
  fields: ReadonlyArray<EntraSyncFieldDiff>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface EntraSyncRemoveRow {
  upn?: string;
  email: string;
  displayName?: string;
  reason?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface EntraSyncDiffOutcome {
  success: boolean;
  adds: ReadonlyArray<EntraUserRow>;
  newRows?: ReadonlyArray<EntraUserRow>;
  updates: ReadonlyArray<EntraSyncUpdateRow>;
  removes: ReadonlyArray<EntraSyncRemoveRow>;
  ignoredCount: number;
  warnings: ReadonlyArray<string>;
  error?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface EntraSyncApplyOutcome {
  success: boolean;
  inserted?: number;
  updated?: number;
  deactivated?: number;
  insertedCount: number;
  updatedCount: number;
  removedCount: number;
  elapsedMs: number;
  errors: ReadonlyArray<string>;
  warnings?: ReadonlyArray<string>;
  error?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface EntraIgnoreInput {
  upn?: string;
  email: string;
  entraObjectId?: string;
  displayName?: string;
  reason?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface EntraIgnoreRow {
  upn?: string;
  email?: string;
  reason?: string;
  createdAt?: Iso8601;
  createdBy?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

// ─────────────────────────────────────────────────────────────────────────
// ParmVal sync panel (Core Director ParmVal mirror)
// ─────────────────────────────────────────────────────────────────────────

export interface ParmValSyncSupportedParm {
  parmName: string;
  displayLabel: string;
  domain?: string;
  description?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface ParmValSyncRow {
  parmName: string;
  code: string;
  label?: string;
  isActive?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface ParmValSyncFieldDiff {
  field?: string;
  fieldName: string;
  oldValue?: string | null;
  newValue?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface ParmValSyncUpdateRow {
  parmName: string;
  code: string;
  fields: ReadonlyArray<ParmValSyncFieldDiff>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface ParmValSyncRemoveRow {
  parmName: string;
  code: string;
  reason?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface ParmValDiffOutcome {
  success: boolean;
  adds: ReadonlyArray<ParmValSyncRow>;
  newRows?: ReadonlyArray<ParmValSyncRow>;
  updates: ReadonlyArray<ParmValSyncUpdateRow>;
  removes: ReadonlyArray<ParmValSyncRemoveRow>;
  warnings: ReadonlyArray<string>;
  error?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface ParmValApplyOutcome {
  success: boolean;
  inserted?: number;
  updated?: number;
  deactivated?: number;
  errors: ReadonlyArray<string>;
  error?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

// ─────────────────────────────────────────────────────────────────────────
// Retention hold (operations panel)
// ─────────────────────────────────────────────────────────────────────────

export interface RetentionHoldRow {
  rowId?: number | string;
  cartId?: string;
  cifId?: string;
  documentId?: string;
  reason?: string;
  status?: string;
  createdAt?: Iso8601;
  createdBy?: string;
  releasedAt?: Iso8601 | null;
  releasedBy?: string | null;
  expiresAt?: Iso8601 | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

// ─────────────────────────────────────────────────────────────────────────
// QC notes (RQC ExceptionDetail)
// ─────────────────────────────────────────────────────────────────────────

export interface QcNoteDto {
  noteId?: number | string;
  exceptionId?: string;
  body?: string;
  authoredBy?: string;
  authoredAt?: Iso8601;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

// ─────────────────────────────────────────────────────────────────────────
// Fraud Dataverse list shape
// ─────────────────────────────────────────────────────────────────────────

export interface FraudCaseDataverseListItem {
  caseId?: string;
  status?: string;
  caseType?: string;
  customer?: string;
  totalAmount?: number;
  createdOn?: Iso8601;
  modifiedOn?: Iso8601;
  ownerUpn?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface FraudCaseRecord {
  caseId?: string;
  name: string;
  /** Numeric enum from FraudCaseTypes catalog. */
  caseType?: number;
  accountType?: number;
  transactionType?: number;
  accountNumber: string;
  phoneNumber: string;
  reportedBy: string;
  status?: string;
  customerCifId?: string;
  customerName?: string;
  totalAmount?: number;
  totalAmountInCents?: number;
  /** Account-history transactions attached to the case. */
  transactions?: ReadonlyArray<FraudTransactionRecord>;
  createdAt?: Iso8601;
  updatedAt?: Iso8601;
  notes?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface FraudTransactionRecord {
  txnId?: string;
  caseId?: string;
  name: string;
  postedDate?: Iso8601;
  amount?: number;
  transactionAmount?: number;
  description?: string;
  txnType?: string;
  isDisputed?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

// ─────────────────────────────────────────────────────────────────────────
// Feature config schema (RAO ServiceToggle + OrderChecksConfigModal)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Single field definition inside a Feature.ConfigSchema JSON blob. The blob
 * is admin-authored, so we keep this very loose: any keys allowed, common
 * fields typed for editor autocomplete.
 */
export interface FeatureConfigPicker {
  kind?: string;
  field?: string;
  /** Required identifier — addressed by the consumer's render loop. */
  key: string;
  label?: string;
  type?: string;
  required?: boolean;
  options?: ReadonlyArray<{ code: string; label: string }>;
  sourceCatalog?: string;
  helpText?: string;
  min?: number;
  max?: number;
  /** Default value for the field — coerced at the render seam. */
  defaultValue?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

export interface FeatureConfigCompound {
  kind?: string;
  /** Schema field list — always populated for compound schemas. */
  fields: ReadonlyArray<FeatureConfigPicker>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

/**
 * Single typed shape covering both picker and compound config schemas. The
 * runtime payload's `kind` discriminator selects which subset of fields is
 * meaningful; the type intentionally collapses both forms into one to make
 * ServiceToggle's render-by-kind branching trivial.
 */
export type FeatureConfigSchema = FeatureConfigPicker & FeatureConfigCompound;

/**
 * Best-effort parser for the JSON blob stored on Feature.ConfigSchema. The
 * actual shape is admin-authored, so we treat the parsed value as a typed
 * `FeatureConfigSchema` once it passes the structural checks and return
 * `null` otherwise — callers fall back to the legacy toggle UI on null.
 */
export function parseFeatureConfigSchema(raw: string | null | undefined): FeatureConfigSchema | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const obj = parsed as Record<string, unknown>;
    // Discriminator is `type` (what every DB Feature.ConfigSchema authors —
    // seeds 68/101). The old code keyed on `kind`, which NO schema carries, so
    // EVERY schema parsed to null and ServiceToggle fell back to a bare boolean
    // — the reason OD Protection had no limit/Reg E and "services don't honor
    // config" (Bill v0.70.0 walk / BACKLOG-0838). `kind` kept as a legacy
    // fallback. Accept the four shapes ServiceToggle actually renders.
    const disc = (typeof obj['type'] === 'string' ? obj['type']
      : typeof obj['kind'] === 'string' ? obj['kind'] : '') as string;
    if (disc === 'compound' && Array.isArray(obj['fields'])) {
      return parsed as FeatureConfigSchema;
    }
    if (disc === 'picker' && typeof obj['field'] === 'string'
        && (Array.isArray(obj['options']) || typeof obj['sourceRef'] === 'string')) {
      return parsed as FeatureConfigSchema;
    }
    if (disc === 'currency' || disc === 'integer') {
      return parsed as FeatureConfigSchema;
    }
    // 2026-07-24 (MacKenzie's e-statements note / Bill's designed-not-wired
    // audit): plain-boolean schemas were REJECTED here, which made
    // ServiceToggle's requiresParent dependency gate dead code — the exact
    // shape `{"type":"boolean","requiresParent":"internetBanking"}` needs.
    // Accept it; a bare boolean with no extras still renders as the legacy
    // toggle, so this is strictly additive.
    if (disc === 'boolean') {
      return parsed as FeatureConfigSchema;
    }
    return null;
  } catch {
    return null;
  }
}

// callClassFor was a STUB here (returned the raw customerType label, which never
// matched product.allowedCallClasses — those are stamped with NUMERIC call-class
// codes). Removed 2026-06-01: the canonical implementation lives in
// ./config/call-class-map.ts (CALL_CLASS_MAP, e.g. Individual → '0') and is now
// exported via ./config/index.ts. Do NOT reintroduce a label-returning stub here.
